<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SidePanel from 'dashboard/components-next/side-panel/SidePanel.vue';
import MetricCard from 'dashboard/components-next/lilith/OverviewMetricCard.vue';
import RangeSelector from 'dashboard/components-next/lilith/OverviewRangeSelector.vue';
import ReportDrilldownCard from 'dashboard/routes/dashboard/settings/reports/components/ReportDrilldownCard.vue';
import { useReportDrilldown } from 'dashboard/routes/dashboard/settings/reports/composables/useReportDrilldown';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { LocalStorage } from 'shared/helpers/localStorage';
import {
  getLilithAssistantDrilldown,
  getLilithAssistantFaqStats,
  getLilithAssistantInboxes,
  getLilithAssistantMetrics,
  getLilithAssistantSummary,
} from 'dashboard/api/lilithTeam';

const props = defineProps({
  assistant: {
    type: Object,
    required: true,
  },
  canDrilldown: {
    type: Boolean,
    default: false,
  },
});

const { accountId } = useAccount();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const snapshotKey = '_snapshot_at';
const selectedRange = ref('7');
const metricStats = ref(null);
const faqStats = ref(null);
const summaryMarkdown = ref('');
const isFetchingMetrics = ref(false);
const isFetchingSummary = ref(false);
const inboxRefs = ref([]);
const isFetchingInboxes = ref(true);
const inboxDismissed = ref(false);
const coverageDismissed = ref(false);

let overviewFetchToken = 0;
let inboxFetchToken = 0;
let summaryAbortController;
let metricsAbortController = null;
let faqAbortController = null;
let inboxAbortController = null;

const knowledge = computed(
  () =>
    faqStats.value || {
      approved: null,
      suggestions: null,
      documents: null,
      coverage: null,
    }
);

const formattedSummary = computed(
  () => new MessageFormatter(summaryMarkdown.value).formattedMessage
);

const resolveTrendGood = (trendValue, direction) => {
  if (direction === 'neutral' || trendValue === 0) return null;
  return direction === 'up' ? trendValue > 0 : trendValue < 0;
};

const TREND_SUFFIX = {
  percent: '%',
  point: ' pts',
  absolute: '',
};

const formatDuration = hours =>
  hours >= 100 ? `${Math.round(hours / 24)}d` : `${hours}h`;

const metricFor = (statKey, formatValue, direction, trendKind = 'percent') => {
  const data = metricStats.value?.[statKey];
  if (!data || data.current === null)
    return { value: '—', trend: '', trendGood: null };

  const trend = Number(data.trend || 0);
  const sign = trend > 0 ? '+' : '';
  return {
    value: formatValue(Number(data.current || 0)),
    trend:
      data.trend == null ? '' : `${sign}${trend}${TREND_SUFFIX[trendKind]}`,
    trendGood: data.trend == null ? null : resolveTrendGood(trend, direction),
  };
};

const metrics = computed(() => [
  {
    key: 'handled',
    metric: 'conversations_handled',
    label: t('TEAM_LILITH.OVERVIEW.METRICS.HANDLED.LABEL'),
    hint: t('TEAM_LILITH.OVERVIEW.METRICS.HANDLED.HINT'),
    ...metricFor(
      'conversations_handled',
      value => value.toLocaleString(),
      'up'
    ),
  },
  {
    key: 'autoResolution',
    metric: 'auto_resolution_rate',
    label: t('TEAM_LILITH.OVERVIEW.METRICS.AUTO_RESOLUTION.LABEL'),
    hint: t('TEAM_LILITH.OVERVIEW.METRICS.AUTO_RESOLUTION.HINT'),
    ...metricFor('auto_resolution_rate', value => `${value}%`, 'up', 'point'),
  },
  {
    key: 'handoff',
    metric: 'handoff_rate',
    label: t('TEAM_LILITH.OVERVIEW.METRICS.HANDOFF.LABEL'),
    hint: t('TEAM_LILITH.OVERVIEW.METRICS.HANDOFF.HINT'),
    ...metricFor('handoff_rate', value => `${value}%`, 'down', 'point'),
  },
  {
    key: 'reopen',
    metric: 'reopen_rate',
    label: t('TEAM_LILITH.OVERVIEW.METRICS.REOPEN.LABEL'),
    hint: t('TEAM_LILITH.OVERVIEW.METRICS.REOPEN.HINT'),
    ...metricFor('reopen_rate', value => `${value}%`, 'down', 'point'),
  },
  {
    key: 'hoursSaved',
    metric: '',
    label: t('TEAM_LILITH.OVERVIEW.METRICS.HOURS_SAVED.LABEL'),
    hint: t('TEAM_LILITH.OVERVIEW.METRICS.HOURS_SAVED.HINT'),
    ...metricFor('hours_saved', formatDuration, 'up'),
  },
  {
    key: 'depth',
    metric: '',
    label: t('TEAM_LILITH.OVERVIEW.METRICS.DEPTH.LABEL'),
    hint: t('TEAM_LILITH.OVERVIEW.METRICS.DEPTH.HINT'),
    ...metricFor(
      'conversation_depth',
      value => value.toFixed(1),
      'neutral',
      'absolute'
    ),
  },
]);

const loadOverview = async () => {
  overviewFetchToken += 1;
  const token = overviewFetchToken;
  summaryAbortController?.abort();
  metricsAbortController?.abort();
  faqAbortController?.abort();
  metricsAbortController = new AbortController();
  faqAbortController = new AbortController();
  metricStats.value = null;
  faqStats.value = null;
  summaryMarkdown.value = '';
  isFetchingMetrics.value = true;
  isFetchingSummary.value = false;

  const requestMetrics = () =>
    getLilithAssistantMetrics(
      accountId.value,
      props.assistant.id,
      selectedRange.value,
      metricsAbortController.signal
    );

  const requestMetricsWithRetry = async () => {
    try {
      return await requestMetrics();
    } catch (error) {
      if (
        token !== overviewFetchToken ||
        metricsAbortController.signal.aborted
      ) {
        throw error;
      }
      return requestMetrics();
    }
  };

  const [metricsResult, faqResult] = await Promise.allSettled([
    requestMetricsWithRetry(),
    getLilithAssistantFaqStats(
      accountId.value,
      props.assistant.id,
      faqAbortController.signal
    ),
  ]);

  if (token !== overviewFetchToken) return;

  metricStats.value =
    metricsResult.status === 'fulfilled' ? metricsResult.value.data : null;
  faqStats.value =
    faqResult.status === 'fulfilled' ? faqResult.value.data : null;
  isFetchingMetrics.value = false;

  if (!metricStats.value) return;

  isFetchingSummary.value = true;
  summaryAbortController = new AbortController();
  try {
    const response = await getLilithAssistantSummary(
      accountId.value,
      props.assistant.id,
      selectedRange.value,
      metricStats.value[snapshotKey],
      summaryAbortController.signal
    );
    if (token === overviewFetchToken) {
      const data = response.data;
      const points = (data?.highlights || []).map(
        point =>
          `- ${t(`TEAM_LILITH.OVERVIEW.SUMMARY_LABELS.${point.metric}`)}: ${point.current} (${t('TEAM_LILITH.OVERVIEW.SUMMARY_PREVIOUS')}: ${point.previous ?? '—'})`
      );
      if (points.length && data.tentative)
        points.push(t('TEAM_LILITH.OVERVIEW.SUMMARY_TENTATIVE'));
      summaryMarkdown.value = points.join('\n\n');
    }
  } catch {
    if (token === overviewFetchToken) summaryMarkdown.value = '';
  } finally {
    if (token === overviewFetchToken) isFetchingSummary.value = false;
  }
};

const loadInboxBindings = async () => {
  inboxFetchToken += 1;
  const token = inboxFetchToken;
  inboxAbortController?.abort();
  inboxAbortController = new AbortController();
  const { signal } = inboxAbortController;
  inboxRefs.value = [];
  isFetchingInboxes.value = true;

  try {
    const response = await getLilithAssistantInboxes(
      accountId.value,
      props.assistant.id,
      signal
    );
    if (token !== inboxFetchToken || signal.aborted) return;
    inboxRefs.value = (response.data?.inbox_refs || []).map(String);
  } catch {
    if (token !== inboxFetchToken || signal.aborted) return;
    inboxRefs.value = [];
  } finally {
    if (token === inboxFetchToken && !signal.aborted) {
      isFetchingInboxes.value = false;
    }
  }
};

watch([selectedRange, accountId, () => props.assistant.id], loadOverview, {
  immediate: true,
});

watch(
  () => props.assistant.id,
  () => {
    loadInboxBindings();
    inboxDismissed.value = false;
    coverageDismissed.value = LocalStorage.getFlag(
      'lilith_overview_coverage_banner',
      accountId.value,
      props.assistant.id
    );
  },
  { immediate: true }
);

onUnmounted(() => {
  overviewFetchToken += 1;
  inboxFetchToken += 1;
  summaryAbortController?.abort();
  metricsAbortController?.abort();
  faqAbortController?.abort();
  inboxAbortController?.abort();
});

const showInboxBanner = computed(
  () =>
    !inboxDismissed.value &&
    !isFetchingInboxes.value &&
    inboxRefs.value.length === 0
);

const showCoverageBanner = computed(
  () =>
    !coverageDismissed.value &&
    Number(knowledge.value.coverage || 0) < 85 &&
    Number(knowledge.value.suggestions || 0) > 100
);

const dismissCoverage = () => {
  LocalStorage.setFlag(
    'lilith_overview_coverage_banner',
    accountId.value,
    props.assistant.id
  );
  coverageDismissed.value = true;
};

const assistantRoute = (name, query = {}) => ({
  name,
  params: {
    accountId: route.params.accountId,
    assistantId: String(props.assistant.id),
  },
  query,
});

const goToInboxes = () => router.push(assistantRoute('lilith_inboxes'));
const goToSuggestions = () =>
  router.push(assistantRoute('lilith_faqs', { tab: 'suggestions' }));

const knowledgeStats = computed(() => [
  {
    key: 'approved',
    value: knowledge.value.approved ?? '—',
    label: t('TEAM_LILITH.OVERVIEW.KNOWLEDGE.APPROVED'),
    to: assistantRoute('lilith_faqs'),
  },
  {
    key: 'suggestions',
    value: knowledge.value.suggestions ?? '—',
    label: t('TEAM_LILITH.OVERVIEW.KNOWLEDGE.SUGGESTIONS'),
    to: assistantRoute('lilith_faqs', { tab: 'suggestions' }),
  },
  {
    key: 'documents',
    value: knowledge.value.documents ?? '—',
    label: t('TEAM_LILITH.OVERVIEW.KNOWLEDGE.DOCUMENTS'),
    to: assistantRoute('lilith_documents'),
  },
]);

const drilldownPanel = ref(null);
const drilldownOpen = ref(false);
const selectedDrilldown = ref({
  metric: '',
  label: '',
  value: '',
});

const {
  records: drilldownRecords,
  meta: drilldownMeta,
  isFetching: isFetchingDrilldown,
  isFetchingMore,
  hasError: drilldownHasError,
  hasRecords: hasDrilldownRecords,
  hasMore: hasMoreDrilldown,
  open: fetchDrilldown,
  close: resetDrilldown,
  loadMore,
} = useReportDrilldown(({ metric, range, page, signal }) =>
  getLilithAssistantDrilldown(
    accountId.value,
    props.assistant.id,
    metric,
    range,
    page,
    signal,
    metricStats.value?.[snapshotKey]
  )
);

const openDrilldown = metric => {
  if (!props.canDrilldown || !metric.metric || isFetchingMetrics.value) return;

  selectedDrilldown.value = {
    metric: metric.metric,
    label: metric.label,
    value: metric.value,
  };
  drilldownOpen.value = true;
  drilldownPanel.value?.open();
  fetchDrilldown({
    metric: metric.metric,
    range: selectedRange.value,
  });
};

const closeDrilldown = () => {
  drilldownOpen.value = false;
  resetDrilldown();
};

watch(selectedRange, () => {
  if (!drilldownOpen.value) return;
  drilldownPanel.value?.close();
  closeDrilldown();
});

watch(
  () => props.canDrilldown,
  allowed => {
    if (allowed || !drilldownOpen.value) return;
    drilldownPanel.value?.close();
    closeDrilldown();
  }
);

watch(
  () => [accountId.value, props.assistant.id],
  () => {
    if (!drilldownOpen.value) return;
    drilldownPanel.value?.close();
    closeDrilldown();
  }
);

const drilldownSubtitle = computed(() => {
  const count = Number(drilldownMeta.value.conversation_count || 0);
  if (!count && !drilldownMeta.value.episode_count) return '';
  return (
    t('TEAM_LILITH.OVERVIEW.DRILLDOWN.RESULT_COUNT', { count }) +
    ' · ' +
    t('TEAM_LILITH.OVERVIEW.DRILLDOWN.EPISODE_COUNT', {
      count: drilldownMeta.value.episode_count || 0,
      unavailable: drilldownMeta.value.unavailable_episode_count || 0,
    })
  );
});

const drilldownRecordKey = record =>
  `${record.conversation?.id || record.conversation?.display_id}-${record.occurred_at}`;
</script>

<template>
  <div class="flex flex-1 overflow-y-auto bg-n-surface-1">
    <div
      class="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-8 lg:px-10"
    >
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div
            class="text-xs font-semibold uppercase tracking-[0.12em] text-n-iris-10"
          >
            {{ $t('TEAM_LILITH.OVERVIEW.EYEBROW') }}
          </div>
          <h1 class="mt-1 text-2xl font-semibold text-n-slate-12">
            {{ $t('TEAM_LILITH.OVERVIEW.TITLE') }}
          </h1>
        </div>
        <RangeSelector v-model="selectedRange" />
      </header>

      <div
        v-if="showInboxBanner"
        class="flex items-center justify-between gap-3 rounded-xl border border-n-amber-4 bg-n-amber-3 px-3 py-2 text-sm text-n-amber-11"
      >
        <span>{{ $t('TEAM_LILITH.OVERVIEW.INBOX_BANNER.TEXT') }}</span>
        <div class="flex items-center gap-1">
          <button
            type="button"
            class="rounded-lg bg-n-amber-4 px-3 py-1 hover:bg-n-amber-5"
            @click="goToInboxes"
          >
            {{ $t('TEAM_LILITH.OVERVIEW.INBOX_BANNER.ACTION') }}
          </button>
          <button
            type="button"
            class="grid size-7 place-content-center rounded-lg hover:bg-n-amber-4"
            :aria-label="$t('TEAM_LILITH.OVERVIEW.INBOX_BANNER.DISMISS')"
            @click="inboxDismissed = true"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>
      </div>

      <div
        v-if="showCoverageBanner"
        class="flex items-center justify-between gap-3 rounded-xl border border-n-amber-4 bg-n-amber-3 px-3 py-2 text-sm text-n-amber-11"
      >
        <span>
          {{
            $t('TEAM_LILITH.OVERVIEW.COVERAGE_BANNER.TEXT', {
              count: knowledge.suggestions,
              coverage: knowledge.coverage,
            })
          }}
        </span>
        <div class="flex items-center gap-1">
          <button
            type="button"
            class="rounded-lg bg-n-amber-4 px-3 py-1 hover:bg-n-amber-5"
            @click="goToSuggestions"
          >
            {{ $t('TEAM_LILITH.OVERVIEW.COVERAGE_BANNER.ACTION') }}
          </button>
          <button
            type="button"
            class="grid size-7 place-content-center rounded-lg hover:bg-n-amber-4"
            :aria-label="$t('TEAM_LILITH.OVERVIEW.COVERAGE_BANNER.DISMISS')"
            @click="dismissCoverage"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>
      </div>

      <p
        v-if="metricStats?.tracking_started_at"
        class="text-xs text-n-slate-10"
      >
        {{
          $t('TEAM_LILITH.OVERVIEW.TRACKING', {
            date: new Date(
              metricStats.tracking_started_at
            ).toLocaleDateString(),
          })
        }}
      </p>
      <section
        v-if="isFetchingSummary || summaryMarkdown"
        class="flex flex-col gap-3"
      >
        <div class="flex items-center gap-1.5 text-n-slate-10">
          <span class="i-lucide-sparkles size-3.5" />
          <span class="text-xs">
            {{ $t('TEAM_LILITH.OVERVIEW.WELCOME.LABEL') }}
          </span>
        </div>
        <div
          v-if="isFetchingSummary"
          class="flex h-16 items-center justify-center"
          :aria-label="$t('TEAM_LILITH.OVERVIEW.WELCOME.LOADING')"
        >
          <Spinner />
        </div>
        <div
          v-else
          v-dompurify-html="formattedSummary"
          class="prose max-w-none text-n-slate-12 prose-p:my-0 prose-p:text-lg prose-p:leading-relaxed prose-strong:text-n-brand"
        />
      </section>

      <div
        class="grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-n-weak bg-n-weak sm:grid-cols-2 lg:grid-cols-3"
      >
        <MetricCard
          v-for="metric in metrics"
          :key="metric.key"
          :label="metric.label"
          :value="metric.value"
          :trend="metric.trend"
          :hint="metric.hint"
          :trend-good="metric.trendGood"
          :loading="isFetchingMetrics"
          :clickable="
            canDrilldown && Boolean(metric.metric) && !isFetchingMetrics
          "
          @click="openDrilldown(metric)"
        />
      </div>

      <section
        class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-5"
      >
        <div class="flex items-center justify-between gap-3">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('TEAM_LILITH.OVERVIEW.KNOWLEDGE.TITLE') }}
          </span>
          <span class="text-sm tabular-nums text-n-slate-11">
            {{
              $t('TEAM_LILITH.OVERVIEW.KNOWLEDGE.COVERAGE', {
                pct: knowledge.coverage || 0,
              })
            }}
          </span>
        </div>
        <div class="h-2 w-full overflow-hidden rounded-full bg-n-alpha-2">
          <div
            class="h-full rounded-full bg-n-brand"
            :style="{ width: `${knowledge.coverage || 0}%` }"
          />
        </div>
        <div class="grid grid-cols-3 gap-3">
          <RouterLink
            v-for="stat in knowledgeStats"
            :key="stat.key"
            :to="stat.to"
            class="flex flex-col gap-1"
          >
            <span class="text-xl font-semibold tabular-nums text-n-slate-12">
              {{ stat.value }}
            </span>
            <span class="text-xs text-n-slate-11 hover:text-n-slate-12">
              {{ stat.label }}
            </span>
          </RouterLink>
        </div>
      </section>
    </div>

    <SidePanel
      v-if="canDrilldown"
      ref="drilldownPanel"
      :title="selectedDrilldown.label"
      width="xl"
      @close="closeDrilldown"
    >
      <template #header>
        <div class="min-w-0">
          <h3 class="truncate text-base font-medium text-n-slate-12">
            {{ selectedDrilldown.label }}
          </h3>
          <p
            v-if="selectedDrilldown.value"
            class="mt-1 text-xl font-semibold text-n-slate-12"
          >
            {{ selectedDrilldown.value }}
          </p>
          <p v-if="drilldownSubtitle" class="mt-1 text-sm text-n-slate-11">
            {{ drilldownSubtitle }}
          </p>
        </div>
      </template>

      <div
        v-if="isFetchingDrilldown"
        class="flex h-40 items-center justify-center"
      >
        <Spinner />
      </div>
      <div
        v-else-if="drilldownHasError"
        class="flex h-40 items-center justify-center text-sm text-n-ruby-11"
      >
        {{ $t('TEAM_LILITH.OVERVIEW.DRILLDOWN.ERROR') }}
      </div>
      <div
        v-else-if="!hasDrilldownRecords"
        class="flex h-40 items-center justify-center text-sm text-n-slate-10"
      >
        {{ $t('TEAM_LILITH.OVERVIEW.DRILLDOWN.EMPTY') }}
      </div>
      <div v-else class="flex flex-col gap-2">
        <ReportDrilldownCard
          v-for="record in drilldownRecords"
          :key="drilldownRecordKey(record)"
          :record="record"
        />
        <Button
          v-if="hasMoreDrilldown"
          faded
          slate
          size="sm"
          class="mx-auto mt-2"
          :label="$t('TEAM_LILITH.OVERVIEW.DRILLDOWN.LOAD_MORE')"
          :is-loading="isFetchingMore"
          @click="loadMore"
        />
      </div>
    </SidePanel>
  </div>
</template>
